import { createSelector } from 'reselect'

import { EMPTY_OBJECT } from 'utils/emptyConstants'
import {
  ALL_COMPANIES_OPTION,
  findAndHighlightFirstInactive,
  generateCompanySelectorObject,
  statusAndNameSortCallback,
} from 'utils/partnerUtils'
import { atLeastCorePlan, exactCompleteProtectPlan } from 'utils/plans'

import { ENGLISH_LANGUAGE, PLAN_TYPES } from 'containers/App/constants'

import { selectCompanyFilter } from '../PartnerOptions/selectors'

const emptyArray = []

export const selectLoader = (state) => state.app.loader
export const selectError = (state) => state.app.error
export const selectIsDataLoaded = (state) => state.app.isDataLoaded

export const selectData = (state) => state.app.data
export const selectCompaniesData = (state) => state.app.data.partner.companies_data

export const selectBrand = (state) => state.app.data.brand

/**
 * This field will exist also for multi tenants
 */
export const selectPartner = (state) => state.app.data.partner
export const selectSortedCompanies = (state) => {
  if (!state.app.data.partner) return emptyArray
  const companies = state.app.data.partner.all_companies
  return companies.sort(statusAndNameSortCallback(true))
}
export const selectSortedCompaniesOptions = createSelector(selectSortedCompanies, (items) => {
  if (items.length) findAndHighlightFirstInactive(items)
  return items.map((elem) => ({ value: elem.id, label: elem.name }))
})
const selectCompanySelectorPartner = (state) => state.app.data.partner
const selectCompanySelectorCompanyId = (state) => state.app.data.id
const selectCompanySelectorCompanies = (state) => state.app.data.partner?.all_companies

export const selectCompanySelectorObject = createSelector(
  selectCompanySelectorPartner,
  selectCompanySelectorCompanyId,
  selectCompanySelectorCompanies,
  (partner, companyId, companies) => {
    if (!partner) return EMPTY_OBJECT
    return generateCompanySelectorObject(companyId, companies)
  }
)

export const selectNotifications = (state) => state.app.data.notifications

export const selectUser = (state) => state.app.data.user
export const selectUserId = (state) => state.app.data.user.id
export const selectUserEmail = (state) => state.app.data.user.email

/**
 * selectSelf* selectors don't support MSP case
 * You have to use them only when you're interested
 * in only signed-in user's company data.
 * ATTENTION: in 99% of cases you have to use selectActual* selectors.
 */
export const selectSelfCompanyId = (state) => state.app.data.id
export const selectSelfCompanyName = (state) => state.app.data.name
export const selectSelfCompanyDomain = (state) => state.app.data.domain
export const selectSelfLicenses = (state) => state.app.data.licenses
export const selectSelfPermissions = (state) => state.app.data.permissions
export const selectSelfIntegrations = (state) => state.app.data.integrations
export const selectSelfFeatureFlags = (state) => state.app.data.ff
export const selectSelfOwnerStatus = (state) => state.app.data.owner
export const selectSelfIsPartner = (state) => state.app.data.isPartner
export const selectSelfPartnerName = (state) => state.app.data.partnerName
export const selectSelfBrandName = (state) => state.app.data.brandName
// Agent enable toggles live in billableAgent on /account/status, NOT in ff
// (get_feature_flags_dict never includes them), and only for the self company.
export const selectBillableAgent = (state) => state.app.data.billableAgent

/**
 * selectActual* selectors support MSP case.
 * Returning data for MSP selected company.
 * For not MSP case returning self data.
 */
export const selectActualCompanyId = (state) =>
  selectCompanyFilter(state)?.value || selectSelfCompanyId(state)

const selectRelevantCompany = createSelector(
  selectActualCompanyId,
  selectCompaniesData,
  (id, companiesData) => companiesData.find((c) => c.id === id)
)

// True when actual-company entitlements are the selected tenant's (not a
// fallback to the partner's own data while MSP child data is still loading).
export const selectIsActualCompanyDataReady = (state) => {
  const id = selectActualCompanyId(state)
  if (!state.app.data.partner || id === selectSelfCompanyId(state)) return true
  const companiesData = selectCompaniesData(state)
  if (!Array.isArray(companiesData)) return false
  return companiesData.some((c) => c.id === id)
}

// if_partner_swap_company 403s unless the selected company is in the user's
// companies. companies_data is the wider partner list, so a loaded brand child
// can still be rejected. hasCompanyAccess is that membership. A payload without
// the flag is allowed through: hiding it would drop data the backend may return.
export const selectHasSelectedCompanyFilterAccess = (state) => {
  if (!state.app?.data?.partner) return true
  const id = selectActualCompanyId(state)
  if (id === selectSelfCompanyId(state)) return true
  const companiesData = state.app.data.partner.companies_data
  if (!Array.isArray(companiesData)) return false
  const company = companiesData.find((c) => c.id === id)
  if (!company) return false
  if (typeof company.hasCompanyAccess !== 'boolean') return true
  return company.hasCompanyAccess
}

const selectActual = (state, field, fallback) => {
  const id = selectActualCompanyId(state)
  if (!state.app.data.partner || id === selectSelfCompanyId(state)) return state.app.data[field]
  else {
    const relevantCompany = selectRelevantCompany(state)

    // data is still loading
    if (!relevantCompany) {
      if (fallback !== undefined) return fallback
      // console.log(
      //   `Provided self company (${state.app.data.id}) data while data of ${id} is loading`
      // )
      return state.app.data[field]
    }

    return relevantCompany[field]
  }
}

export const selectActualLicenses = (state) => selectActual(state, 'licenses')
export const selectActualPermissions = (state, fallback) =>
  selectActual(state, 'permissions', fallback)
export const selectActualCompanyName = (state) => selectActual(state, 'name')
export const selectActualLogo = (state) => selectActual(state, 'logo')
export const selectActualIntegrations = (state) => selectActual(state, 'integrations')
// `?new-nav=1` / `?new-nav=0` force the new navigation on or off, overriding the
// company's NEW_PLATFORM_NAV flag; `?new-nav=auto` (or any other value) drops the
// override and follows the flag again. `?new-nav=1` also force-enables the
// redesigned Incidents page (ENABLE_INCIDENTS_PAGE_REDESIGN_FF) FE-only, so the
// new nav can be previewed together with the page built for it on any tenant.
// Persisted because SPA navigation drops the query string on the first route
// change, and in localStorage rather than session scope so one link holds for
// every tab and survives a browser restart — it is sticky by design, and
// `?new-nav=auto` is the only way out.
const NAV_OVERRIDE_KEY = 'newNavOverride'

const readNavOverride = () => {
  try {
    return window.localStorage.getItem(NAV_OVERRIDE_KEY)
  } catch {
    // Storage blocked (private mode): the query string still works per page load.
    return null
  }
}

const writeNavOverride = (value) => {
  try {
    if (value === null) window.localStorage.removeItem(NAV_OVERRIDE_KEY)
    else window.localStorage.setItem(NAV_OVERRIDE_KEY, value)
    return true
  } catch {
    // Storage blocked: the override still applies to this page load.
    return false
  }
}

const resolveNavOverride = () => {
  if (typeof window === 'undefined') return null
  const param = new URLSearchParams(window.location.search).get('new-nav')
  if (param === null) {
    const stored = readNavOverride()
    return stored === null ? null : stored === '1'
  }
  const next = param === '1' || param === '0' ? param : null
  writeNavOverride(next)
  return next === null ? null : next === '1'
}

const navUrlOverride = resolveNavOverride()

// The redesigned Incidents page ships with the new nav: `?new-nav=1` turns it on
// FE-only so both can be previewed together on any tenant; otherwise it follows
// the company's backend flag.
export const selectIncidentsRedesignEnabled = (state) =>
  navUrlOverride === true || !!state.app.data.ff?.ENABLE_INCIDENTS_PAGE_REDESIGN_FF

// NEW_PLATFORM_NAV is a chrome flag: it follows the logged-in company (self),
// not the selected MSP child, so picking a tenant does not swap the sidebar.
// Precedence: company flag < `?new-nav` override < Storybook's
// window.__SB_NEW_PLATFORM_NAV__ (kept highest so stories stay deterministic).
// Memoized per `ff` object identity (not a single global slot): different
// consumers call this with different `fallback` args in the same render pass,
// and a single mutable slot thrashes between their inputs, which makes
// react-redux 8's useSyncExternalStore see "same params, different result"
// for whichever consumer loses the race. Caching by `ff` reference (with the
// resolved `nav` as a secondary key) keeps every consumer's result stable
// regardless of interleaving, and still avoids re-rendering every connected
// consumer on a fresh `ff` object per call (e.g. Settings fetchGroupSettingsName).
const actualFeatureFlagsCache = new WeakMap()
// `ff` is absent until FETCH_APP_DATA_ASYNC.SUCCESS (or when the response omits it);
// the nav-only result for that window must be stable too, or every consumer
// re-renders on unrelated state changes during boot.
const missingFeatureFlagsCache = new Map()
export const selectActualFeatureFlags = (state, fallback) => {
  const ff = selectActual(state, 'ff', fallback)
  let nav = !!state.app?.data?.ff?.NEW_PLATFORM_NAV
  if (navUrlOverride !== null) {
    nav = navUrlOverride
  }
  // eslint-disable-next-line no-underscore-dangle
  if (typeof window !== 'undefined' && typeof window.__SB_NEW_PLATFORM_NAV__ === 'boolean') {
    // eslint-disable-next-line no-underscore-dangle
    nav = window.__SB_NEW_PLATFORM_NAV__
  }
  if (!ff || typeof ff !== 'object') {
    if (!missingFeatureFlagsCache.has(nav)) {
      missingFeatureFlagsCache.set(nav, { NEW_PLATFORM_NAV: nav })
    }
    return missingFeatureFlagsCache.get(nav)
  }
  let navCache = actualFeatureFlagsCache.get(ff)
  if (!navCache) {
    navCache = new Map()
    actualFeatureFlagsCache.set(ff, navCache)
  }
  if (!navCache.has(nav)) {
    navCache.set(nav, { ...ff, NEW_PLATFORM_NAV: nav })
  }
  return navCache.get(nav)
}
export const selectActualTags = (state) => selectActual(state, 'tags')
export const selectActualOwnerStatus = (state) => selectActual(state, 'owner')
export const selectActualLanguages = (state) => selectActual(state, 'languages')
export const selectActualEnabledLanguages = createSelector(selectActualLanguages, (items) =>
  items.filter((l) => l.enabled)
)
export const selectActualEnabledLanguagesOptions = createSelector(
  selectActualEnabledLanguages,
  (items) => items.map((elem) => ({ value: elem.id, label: elem.name }))
)
export const selectActualDefaultLanguage = createSelector(selectActualLanguages, (items) =>
  items.find((l) => l.default)
)
export const selectActualWorkdays = (state) => selectActual(state, 'workdays')
export const selectActualTimezone = (state) => selectActual(state, 'timezone')
export const selectActualScanBackStatus = (state) => selectActual(state, 'scanBackStatus')
export const selectActualShowReintegratePopup = (state) =>
  selectActual(state, 'showReintegrationPopup')

export const selectAllActualProperties = createSelector(
  selectActualLicenses,
  selectActualPermissions,
  selectActualIntegrations,
  selectActualFeatureFlags,
  (licenses, permissions, integrations, ff) => ({
    licenses,
    permissions,
    integrations,
    ff,
  })
)

export const selectActualBrandName = (state) => selectActual(state, 'brandName')
export const selectActualIsPartner = (state) => selectActual(state, 'isPartner')

// billableAgent only exists for the self company (/account/status) — child companies_data has
// no billableAgent field. Unlike selectActual(), we must NOT fall back to the self company's
// billableAgent while a child's data is loading, since that would wrongly apply the partner's
// own toggles to the child; undefined is the correct value for a selected child tenant.
export const selectActualBillableAgent = (state) => {
  if (!state.app.data.partner || selectActualCompanyId(state) === selectSelfCompanyId(state)) {
    return selectBillableAgent(state)
  }
  return undefined
}

export const selectActualHasLimitedPocView = (state) => selectActual(state, 'poc_limit')

export const selectActualMicDefenderVisibilityEligible = (state) =>
  selectActual(state, 'micDefenderVisibilityEligible')

/**
 * Returning name of partner above in hierarchy.
 * For example for Vector (767) will return Pax8 (733)
 */
export const selectActualPartnerName = (state) => selectActual(state, 'partnerName')

// ONPREMISE, PROD, STAGING
export const selectEnvironment = (state) => state.app.data.environment
export const selectIsOnPremise = (state) => state.app.data.environment === 'ONPREMISE'

// ENABLE_BABLIC_FF — gates the Bablic moxie script (see utils/thirdPartyScripts).
export const selectAllowBablic = (state) => state.app.data.allow_bablic
// Gates the Zendesk support launcher; false for on-premise and for master users.
export const selectShowSupportButton = (state) => state.app.data.show_support_button
// US, EU, OTHER
export const selectRegion = (state) => state.app.data.region

export const selectIsMaster = (state) => state.app.data.master
export const selectMasterFeatures = (state) => state.app.data.masterFeatures || EMPTY_OBJECT

export const selectIsAdmin = (state) => state.app.data.permissions.admin

export const selectActualMailboxLimit = (state) => selectActual(state, 'mailboxLimit')
export const selectActualMailboxEnabled = (state) => selectActual(state, 'mailboxEnabled')

export const selectStatusOfIrontrapsLicensesAndPerm = (state) =>
  atLeastCorePlan(state.app.data.licenses.plan) &&
  (state.app.data.permissions.irontraps_viewer ||
    state.app.data.permissions.manage_irontraps ||
    state.app.data.permissions.irontraps_action)

export const selectIsAPIGenerated = (state) => state.app.data.is_api_generated

export const selectBusinessPillar = (state) => state.app.data.business_pillar

export const selectLastProductRelease = (state) => state.app.data.last_product_release
export const selectLastSeenProductRelease = (state) => state.app.data.product_release_last_seen_date
export const selectDisablePremiumModal = (state) => state.app.data.disable_premium_popup
export const selectIsPax8Hierarchy = (state) => state.app.data.isPax8Hierarchy
export const selectIsPartnerSuccessSubmissionEnabled = (state) =>
  state.app.data.isPartnerSuccessSubmissionEnabled
export const selectIsFirstCampaignCreated = (state) =>
  state.app.data.setupStatus.created_first_campaign
export const selectIsIneligibleTrainingCampaignsAccount = createSelector(
  selectActualFeatureFlags,
  selectActualLicenses,
  (ff, licenses) =>
    !ff.TRAINING_CAMPAIGNS &&
    ![PLAN_TYPES.SAT_SUITE, PLAN_TYPES.COMPLETE_PROTECT].includes(licenses.plan)
)
export const selectIsSpearPhishingAvailable = createSelector(
  selectActualFeatureFlags,
  selectActualEnabledLanguages,
  selectActualLicenses,
  selectRegion,
  (ff, enabledLanguages, licenses, region) => {
    const isEnglishLanguageEnabled = !!enabledLanguages.find(
      ({ name }) => name === ENGLISH_LANGUAGE.name
    )
    const isFeatureEnabled = ff.SPEAR_PHISHING_CAMPAIGNS || exactCompleteProtectPlan(licenses.plan)

    return isFeatureEnabled && isEnglishLanguageEnabled && region !== 'CA'
  }
)
export const selectIsIneligibleTriggerBasedCampaignsAccount = createSelector(
  selectActualFeatureFlags,
  selectActualLicenses,
  selectActualIntegrations,
  (ff, licenses, integrations) => {
    const hasRequiredFeatureOrPlan =
      ff.TRIGGER_BASED_CAMPAIGNS ||
      [PLAN_TYPES.SAT_SUITE, PLAN_TYPES.COMPLETE_PROTECT].includes(licenses.plan)

    const hasRequiredIntegration = integrations.gmail_webhooks || integrations.o365

    return !(hasRequiredFeatureOrPlan && hasRequiredIntegration)
  }
)
export const selectIsAllCompaniesSelected = (state) =>
  selectPartner(state) && selectCompanyFilter(state)?.value === ALL_COMPANIES_OPTION.value
export const selectEula = (state) => state.app.data.eula
export const selectIsFirstLogin = (state) => state.app.data.eula.is_first_login
export const selectShowEulaModal = (state) => state.app.data.eula.update_required
export const selectEulaType = (state) => state.app.data.eula.eula_type
export const SelectFirstLoginCountry = (state) => state.app.data.eula.country
export const selectDeepfakeActivationTime = (state) => state.app.data.deepfake.activation_time
export const selectDeepfakeAutoLearningEnabled = (state) =>
  state.app.data.deepfake.auto_learning_enabled
export const selectDeepfakeAutoSelectiveLearningEnabled = (state) =>
  state.app.data.deepfake.auto_selective_learning_enabled
export const selectDeepfakeVoiceDetectionEnabled = (state) =>
  state.app.data.deepfake.voice_detection_enabled
export const selectHasDeepfakePresumeSafeAccess = (state) =>
  Boolean(selectActualPermissions(state)?.deepfake_presume_safe_access)

/**
 *
 * @param {Object} state
 * @returns {boolean}
 * @description Checks if the user switched to the static demo user/company.
 * MSP is not supported as the static user/company in this scenario are not MSP.
 */
export const selectIsStaticSalesDemo = (state) => state.app.data.isStaticSalesDemo

export const selectScanbackPeriod = (state) => selectActual(state, 'scanBackPeriod')
export const selectDefaultIncidentDateFilter = (state) =>
  selectActual(state, 'defaultIncidentDateFilter')

export const selectLastScanbackDate = (state) => selectScanbackPeriod(state)?.end_date

/**
 * Checks if customer access is restricted during POV cleanup. MSP/partner out of scope.
 * Master users (IM team) bypass this restriction.
 */
export const selectIsCustomerAccessRestricted = createSelector(
  selectActualFeatureFlags,
  selectIsMaster,
  (ff, isMaster) => ff?.RESTRICTED_ACCESS_FF && !isMaster
)

/**
 * Returns true when the current tenant is in a Proof of Value (POV) period.
 * POV tenants have unlimited AI credit access and are exempt from billing.
 */
export const selectIsPovTenant = createSelector(selectActualFeatureFlags, (ff) =>
  Boolean(ff?.POV_COSTUMER_FF)
)

/**
 * True when Credit Management meters this tenant count-only (`tenants.billing_bypass`),
 * so it is never charged and no billing surface applies to it.
 *
 * BILLING_BYPASS is a synthetic flag: the backend derives it from CM on
 * /account/status rather than from a FeatureFlag row, and it FAILS SAFE TO TRUE —
 * an unreachable CM or an unprovisioned tenant reports bypassed, so we hide
 * billing rather than show charges the tenant may not owe.
 */
export const selectIsBillingBypassed = createSelector(selectActualFeatureFlags, (ff) =>
  Boolean(ff?.BILLING_BYPASS)
)
